/**
 * Event Utility Functions
 * Centralized functions for dispatching and listening to custom events
 */

/**
 * Dispatch a custom event on the window object
 * 
 * @param eventName - Name of the event
 * @param detail - Optional detail object to pass with the event
 */
export function dispatchCustomEvent(eventName: string, detail?: any): void {
  if (typeof window === 'undefined')
    return

  const event = new CustomEvent(eventName, { detail })
  window.dispatchEvent(event)
}

/**
 * Add event listener for custom events with automatic cleanup
 * 
 * @param eventName - Name of the event to listen for
 * @param handler - Event handler function
 * @returns Cleanup function to remove the event listener
 */
export function onCustomEvent(eventName: string, handler: (event: CustomEvent) => void): () => void {
  if (typeof window === 'undefined')
    return () => {}

  const listener = (event: Event) => handler(event as CustomEvent)
  window.addEventListener(eventName, listener)

  return () => window.removeEventListener(eventName, listener)
}

/**
 * Add event listener that automatically cleans up when component unmounts
 * Use this with Vue's onUnmounted
 * 
 * @param eventName - Name of the event to listen for
 * @param handler - Event handler function
 */
export function useCustomEvent(eventName: string, handler: (event: CustomEvent) => void): void {
  if (typeof window === 'undefined')
    return

  const listener = (event: Event) => handler(event as CustomEvent)
  window.addEventListener(eventName, listener)

  // Note: Caller should use onUnmounted(() => cleanup()) in Vue components
  // Or call the cleanup function returned by onCustomEvent
}

